/**
 * MySubmissionsPage — `/submit/mine`
 *
 * The "everyone" self-service view of the Submit audit log. Any signed-in user
 * can see the events THEY submitted and what happened to each one — accepted
 * into the review queue, or errored — without needing admin access. Backed by
 * `GET /api/submissions/mine`, which scopes rows to the caller's identity.
 *
 * Admins get the global, cross-user view at `/submit/history`
 * (`AdminSubmissionsPage`); both render the shared `SubmissionsTable`.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SectionTabs from '../components/SectionTabs';
import { SUBMIT_TABS } from '../lib/section-tabs';
import { api } from '../lib/api';
import { LoadingBlock } from '../components/Spinner';
import SubmissionsTable, {
  type SubmissionStatus,
  type SubmissionsResponse
} from '../components/SubmissionsTable';

export default function MySubmissionsPage() {
  const [data, setData] = useState<SubmissionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [status, setStatus] = useState<'all' | SubmissionStatus>('all');
  const [monthsBack, setMonthsBack] = useState(3);

  const reload = useMemo(
    () => () => {
      setLoading(true);
      setErr(null);
      const params = new URLSearchParams();
      params.set('limit', '200');
      params.set('monthsBack', String(monthsBack));
      if (status !== 'all') params.set('status', status);
      api
        .get<SubmissionsResponse>(`/api/submissions/mine?${params.toString()}`)
        .then(setData)
        .catch((e) => setErr(String(e)))
        .finally(() => setLoading(false));
    },
    [status, monthsBack]
  );

  useEffect(() => { reload(); }, [reload]);

  const items = data?.items ?? [];

  return (
    <SectionTabs ariaLabel="Submit sections" items={SUBMIT_TABS}>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h1 className="mb-0">My submissions</h1>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={reload}
          disabled={loading}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      <p className="text-muted small">
        Everything you've submitted through the Submit page. New submissions are
        added to a review queue and only appear on the public calendar once an
        admin approves them — so a <strong>Success</strong> here means it was
        accepted for review, not that it's live yet.
      </p>

      {/* Filters */}
      <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
        <div>
          <label className="form-label small mb-1" htmlFor="my-sub-status">Status</label>
          <select
            id="my-sub-status"
            className="form-select form-select-sm"
            value={status}
            onChange={(e) => setStatus(e.target.value as 'all' | SubmissionStatus)}
          >
            <option value="all">All</option>
            <option value="success">Success</option>
            <option value="error">Error</option>
          </select>
        </div>
        <div>
          <label className="form-label small mb-1" htmlFor="my-sub-months">Months back</label>
          <select
            id="my-sub-months"
            className="form-select form-select-sm"
            value={monthsBack}
            onChange={(e) => setMonthsBack(Number(e.target.value))}
          >
            {[1, 2, 3, 6, 12].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
      </div>

      {err && <div className="alert alert-danger" role="alert">{err}</div>}
      {loading && !data && <LoadingBlock label="Loading your submissions…" />}

      {data && items.length === 0 && (
        <div className="alert alert-info" role="status">
          You haven't submitted anything yet. Head to{' '}
          <Link to="/submit">Submit an event</Link> to add one.
        </div>
      )}

      {items.length > 0 && <SubmissionsTable items={items} showUser={false} />}
    </SectionTabs>
  );
}
